
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import SampleCode from '../../../../components/docs/plaidoms/SampleCode';

export const metadata = {
  toc: true,
  layout: 'guide',
  maxTocLevel: 4,
  description: 'Use Ocrolus with Plaid Assets to digitize data collection'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Add Ocrolus to your app' subtitle={metadata.description} mdxType="Header" />
    <Image src='/assets/img/docs/plaid-ocrolus-partnership.png' alt="Logos of Plaid and Ocrolus side by side." width='624' mdxType="Image" />
    <br />
    <p>{`Plaid and Ocrolus have partnered to offer lenders an easier way to access bank data to make informed loan decisions. Plaid enables businesses to instantly connect a customer's bank account, giving them the ability to authenticate and retrieve account details directly from the financial institution. Ocrolus digitizes bank and credit card statements from all US financial institutions to help lenders digitize their data collection for cash-flow analysis.`}</p>
    <p>{`With the Plaid + Ocrolus integration, your users can verify their accounts in seconds by inputting their banking credentials in Plaid's front-end module. Plaid will retrieve the relevant bank information and pass it to Ocrolus for further digestion and reporting in a seamless, secure fashion. Plaid's mobile-friendly module handles input validation, error handling, and multi-factor authentication, providing a seamless onboarding experience to convert more users for your business.`}</p>
    <h4 {...{
      "id": "getting-started"
    }}>{`Getting started`}</h4>
    <p>{`You'll first want to familiarize yourself with `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Plaid Link`}</a>{`, a drop-in integration for the Plaid API that handles input validation, error handling, and multi-factor authentication. You will also need to create or be an existing `}<a parentName="p" {...{
        "href": "https://app.ocrolus.com/signup"
      }}>{`Ocrolus customer`}</a>{` in order to add a bank account.`}</p>
    <p>{`Your customers will use Link to authenticate with their financial institution and select the bank account they wish to use for payment and verification of assets. From there, you'll receive a Plaid `}<inlineCode parentName="p">{`access_token`}</inlineCode>{`, which you can use to generate an Ocrolus `}<inlineCode parentName="p">{`processor_token`}</inlineCode>{` and/or `}<inlineCode parentName="p">{`audit_copy_token`}</inlineCode>{`, depending on your use case, which allow you to quickly and securely verify banking information via the `}<a parentName="p" {...{
        "href": "https://docs.ocrolus.com/"
      }}>{`Ocrolus API`}</a>{` without having to store that sensitive information yourself.`}</p>
    <h4 {...{
      "id": "instructions"
    }}>{`Instructions`}</h4>
    <h5 {...{
      "id": "set-up-your-plaid-and-ocrolus-accounts"
    }}>{`Set up your Plaid and Ocrolus accounts`}</h5>
    <p>{`You'll need accounts at both Plaid and Ocrolus in order to use the Plaid + Ocrolus integration. You'll also need to enable your Plaid account for the Ocrolus integration.`}</p>
    <p>{`First, you will need to work with the Ocrolus team to `}<a parentName="p" {...{
        "href": "https://app.ocrolus.com/signup"
      }}>{`sign up for an Ocrolus account`}</a>{`, if you do not already have one.`}</p>
    <p>{`Next, verify that your Plaid account is enabled for the integration. If you do not have a Plaid account, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/signup/ocrolus"
      }}>{`create one`}</a>{`. Your account will be automatically enabled for integration access.`}</p>
    <p>{`To verify that your Plaid account is enabled for the integration, go to the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/integrations"
      }}>{`Integrations`}</a>{` section of the account dashboard. If the integration is off, simply click the 'Enable' button for Ocrolus to enable the integration.`}</p>
    <h5 {...{
      "id": "create-a-link_token"
    }}>{`Create a link_token`}</h5>
    <p>{`In order to integrate with Plaid Link, you will first need to create a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`. A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`
is a short-lived, one-time use token that is used to authenticate your app with Link. To create one,
make a `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` request with your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, and a few other
required parameters from your app server. View the `}<a parentName="p" {...{
        "href": "/docs/api/link/#linktokencreate"
      }}>{`documentation`}</a>{` for a full list of `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` configurations.`}</p>
    <p>{`To see your `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{`, visit the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/keys"
      }}>{`Plaid Dashboard`}</a>{`.`}</p>
    <SampleCode.CreateLinkToken product='assets' />
    <h5 {...{
      "id": "integrate-with-plaid-link"
    }}>{`Integrate with Plaid Link`}</h5>
    <p>{`Once you have a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`, all it takes is a few lines of client-side JavaScript to launch Link.
Then, in the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback, you can call a simple server-side handler to exchange the Link `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`
for a Plaid `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` and an Ocrolus `}<inlineCode parentName="p">{`processor_token`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript",
        "metastring": "#Integrate Link",
        "#Integrate": true,
        "Link": true
      }}>{`<button id="linkButton">Open Link - Institution Select</button>
<script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
<script>
  (async function(){
    var linkHandler = Plaid.create({
      // Make a request to your server to fetch a new link_token.
      token: (await $.post('/create_link_token')).link_token,
      onLoad: function() {
          // The Link module finished loading.
      },
      onSuccess: function(public_token, metadata) {
        // The onSuccess function is called when the user has
        // successfully authenticated and selected an account to
        // use.
        //
        // When called, you will send the public_token
        // and the selected accounts, metadata.accounts,
        // to your backend app server.
        //
        // sendDataToBackendServer({
        //   public_token: public_token,
        //   accounts: metadata.accounts
        // });
        console.log('Public Token: ' + public_token);
        console.log('Customer-selected account IDs: ' + metadata.accounts.map(account => account.id).join(', '));
      },
      onExit: function(err, metadata) {
        // The user exited the Link flow.
        if (err != null) {
            // The user encountered a Plaid API error
            // prior to exiting.
        }
        // metadata contains information about the institution
        // that the user selected and the most recent
        // API request IDs.
        // Storing this information can be helpful for support.
      },
    });
  })();

  // Trigger the authentication view
  document.getElementById('linkButton').onclick = function() {
    linkHandler.open();
  };
</script>
`}</code></pre>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/docs/link/web#create"
      }}>{`Link parameter reference`}</a>{` for complete documentation on possible configurations.`}</p>
    <p><inlineCode parentName="p">{`Plaid.create`}</inlineCode>{` accepts one argument, a configuration `}<inlineCode parentName="p">{`Object`}</inlineCode>{`, and returns an `}<inlineCode parentName="p">{`Object`}</inlineCode>{` with four functions, `}<a parentName="p" {...{
        "href": "/docs/link/web/#open"
      }}><inlineCode parentName="a">{`open`}</inlineCode></a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/web/#exit"
      }}><inlineCode parentName="a">{`exit`}</inlineCode></a>{`, `}<a parentName="p" {...{
        "href": "/docs/link/web/#submit"
      }}><inlineCode parentName="a">{`submit`}</inlineCode></a>{`, and `}<a parentName="p" {...{
        "href": "/docs/link/web/#destroy"
      }}><inlineCode parentName="a">{`destroy`}</inlineCode></a>{`. Calling `}<inlineCode parentName="p">{`open`}</inlineCode>{` will display the "Institution Select" view, calling `}<inlineCode parentName="p">{`exit`}</inlineCode>{` will close Link, and calling `}<inlineCode parentName="p">{`destroy`}</inlineCode>{` will clean up the iframe.`}</p>
    <h5 {...{
      "id": "write-server-side-handler"
    }}>{`Write server-side handler`}</h5>
    <p>{`The Link module handles the entire onboarding flow securely and quickly, but does not actually retrieve account data for a user. Instead, the Link module returns a `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` and an `}<inlineCode parentName="p">{`accounts`}</inlineCode>{` array, which is a property on the `}<inlineCode parentName="p">{`metadata`}</inlineCode>{` object, via the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback. Exchange this `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for a Plaid `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` using the `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` API endpoint.`}</p>
    <p>{`Once you have the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` for the Item, you can create an Ocrolus `}<inlineCode parentName="p">{`processor_token`}</inlineCode>{` and/or `}<inlineCode parentName="p">{`audit_copy_token`}</inlineCode>{`. You'll send these tokens to Ocrolus and they will use them to securely retrieve banking information from Plaid.`}</p>
  <MDXCodeBlock type='server' title='Create Ocrolus processor and Audit Copy tokens' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const {
  Configuration,
  PlaidApi,
  PlaidEnvironments,
  ProcessorTokenCreateRequest,
  AssetReportCreateRequest,
  AssetReportAuditCopyCreateRequest,
} = require('plaid');
// Change sandbox to production to test with live users or to go live!
const configuration = new Configuration({
  basePath: PlaidEnvironments[process.env.PLAID_ENV],
  baseOptions: {
    headers: {
      'PLAID-CLIENT-ID': process.env.PLAID_CLIENT_ID,
      'PLAID-SECRET': process.env.PLAID_SECRET,
      'Plaid-Version': '2020-09-14',
    },
  },
});

const plaidClient = new PlaidApi(configuration);

try {
  // Exchange the public_token from Plaid Link for an access token.
  const tokenResponse = await plaidClient.itemPublicTokenExchange({
    public_token: PUBLIC_TOKEN,
  });
  const accessToken = tokenResponse.data.access_token;

  // Create a processor token for a specific account id.
  const request: ProcessorTokenCreateRequest = {
    access_token: accessToken,
    account_id: accountID,
    processor: 'ocrolus',
  };
  const processorTokenResponse = await plaidClient.processorTokenCreate(
    request,
  );
  const processorToken = processorTokenResponse.data.processor_token;

  // Create an Asset Report for the specific access token.
  const assetReportRequest: AssetReportCreateRequest = {
    access_tokens: [accessToken],
    days_requested: 90,
    options: {},
  };
  const response = await plaidClient.assetReportCreate(assetReportRequest);
  const assetReportId = response.data.asset_report_id;
  const assetReportToken = response.data.asset_report_token;

  // Create an audit copy token for the Asset Report.
  const auditCopyRequest: AssetReportAuditCopyCreateRequest = {
    asset_report_token: response.data.asset_report_token,
    auditor_id: 'ocrolus',
  };
  const auditCopyResponse = await plaidClient.assetReportAuditCopyCreate(auditCopyRequest);
  const auditCopyToken = auditCopyResponse.data.audit_copy_token;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`# Exchange the public token from Plaid Link for an access token.
curl \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "public_token": "\${PUBLIC_TOKEN}"
  }' \\
  -X POST \\
  https://sandbox.plaid.com/item/public_token/exchange

# Create a processor token for a specific account id.
curl \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "access_token": "\${ACCESS_TOKEN}",
    "account_id": "\${ACCOUNT_ID}",
    "processor": "ocrolus"
  }' \\
  -X POST \\
  https://sandbox.plaid.com/processor/token/create

# Create an Asset Report for the specific access token.
curl \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "access_tokens": ["\${ACCESS_TOKEN}"],
    "days_requested": 90
  }' \\
  -X POST \\
  https://sandbox.plaid.com/asset_report/create

# Create an audit copy token for the Asset Report.
curl \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "asset_report_token": "\${ASSET_REPORT_TOKEN}",
    "auditor_id": "ocrolus"
  }' \\
  -X POST \\
  https://sandbox.plaid.com/asset_report/audit_copy/create
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`require 'plaid'

# Change sandbox to production to test with live users or to go live!
configuration = Plaid::Configuration.new
configuration.server_index = Plaid::Configuration::Environment[ENV['PLAID_ENV'] || 'sandbox']
configuration.api_key['PLAID-CLIENT-ID'] = ENV['PLAID_CLIENT_ID']
configuration.api_key['PLAID-SECRET'] = ENV['PLAID_SECRET']
configuration.api_key['Plaid-Version'] = '2020-09-14'

api_client = Plaid::ApiClient.new(
  configuration
)

client = Plaid::PlaidApi.new(api_client)

# Exchange the public token from Plaid Link for an access token.
item_public_token_exchange_request = Plaid::ItemPublicTokenExchangeRequest.new(
  {
    public_token: public_token
  }
)
exchange_token_response = client.item_public_token_exchange(item_public_token_exchange_request)
access_token = exchange_token_response.access_token

# Create a processor token for a specific account id.
processor_token_create_request = Plaid::ProcessorTokenCreateRequest.new(
  {
    access_token: access_token,
    account_id: account_id,
    processor: "ocrolus"
  }
)
create_response = client.processor_token_create(processor_token_create_request)
processor_token = create_response.processor_token

# Create an Asset Report for the specific access token.
options = {
  client_report_id: '123',
  webhook: 'https://www.example.com',
  user: {
    client_user_id: '7f57eb3d2a9j6480121fx361',
    first_name: 'Jane',
    middle_name: 'Leah',
    last_name: 'Doe',
    ssn: '123-45-6789',
    phone_number: '(555) 123-4567',
    email: 'jane.doe@example.com',
  },
}
request = Plaid::AssetReportCreateRequest.new(
  {
    access_tokens: [access_token],
    days_requested: 90,
    options: options
  }
)
response = client.asset_report_create(request)
asset_report_id = response.asset_report_id
asset_report_token = response.asset_report_token

# Create an audit copy token for the Asset Report.
request = Plaid::AssetReportAuditCopyCreateRequest.new(
  {
    asset_report_token: asset_report_token,
    auditor_id: 'ocrolus'
  }
)
response = client.asset_report_audit_copy_create(request)
audit_copy_token = response.audit_copy_token
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`import com.plaid.client.model.ItemPublicTokenExchangeRequest;
import com.plaid.client.model.ItemPublicTokenExchangeResponse;
import com.plaid.client.model.ProcessorTokenCreateRequest;
import com.plaid.client.model.ProcessorTokenCreateResponse;
import com.plaid.client.model.AssetReportCreateRequest;
import com.plaid.client.model.AssetReportUser;
import com.plaid.client.model.AssetReportCreateRequestOptions;
import com.plaid.client.model.AssetReportCreateResponse;
import com.plaid.client.model.AssetReportAuditCopyCreateRequest;
import com.plaid.client.model.AssetReportAuditCopyCreateResponse;

// Change sandbox to production to test with live users or to go live!
HashMap<String, String> apiKeys = new HashMap<String, String>();
apiKeys.put("clientId", plaidClientId);
apiKeys.put("secret", plaidSecret);
apiKeys.put("plaidVersion", "2020-09-14");
apiClient = new ApiClient(apiKeys);
apiClient.setPlaidAdapter(ApiClient.Sandbox);

plaidClient = apiClient.createService(PlaidApi.class);

// Exchange the public token from Plaid Link for an access token.
ItemPublicTokenExchangeRequest exchangeRequest = new ItemPublicTokenExchangeRequest()
  .publicToken(publicToken);

Response<ItemPublicTokenExchangeResponse> exchangeResponse = plaidClient
  .itemPublicTokenExchange(exchangeRequest)
  .execute();

// Create a processor token for a specific account id.
String accessToken = null;
if (exchangeResponse.isSuccessful()) {
  accessToken = exchangeResponse.body().getAccessToken();
  ProcessorTokenCreateRequest processorRequest = new ProcessorTokenCreateRequest()
    .accessToken(accessToken)
    .processor(ProcessorTokenCreateRequest.ProcessorEnum.OCROLUS)
    .accountId("FooBarAccountId");

  Response<ProcessorTokenCreateResponse> processorResponse = plaidClient
    .processorTokenCreate(processorRequest)
    .execute();

    if (processorResponse.isSuccessful()) {
      String processorToken = processorResponse.body().getProcessorToken();
    }
}

// Create an Asset Report for the specific access token.
AssetReportCreateRequest assetReportCreateRequest = new AssetReportCreateRequest()
  .accessTokens(Arrays.asList(accessToken))
  .daysRequested(90);

AssetReportUser assetReportUser = new AssetReportUser()
  .firstName("Alberta")
  .middleName("Bobbeth")
  .lastName("Charleson");

AssetReportCreateRequestOptions assetReportCreateOptions = new AssetReportCreateRequestOptions()
  .user(assetReportUser)
  .webhook(webhookUrl);

assetReportCreateRequest.options(assetReportCreateOptions);

Response<AssetReportCreateResponse> response = plaidClient
  .assetReportCreate(assetReportCreateRequest)
  .execute();

String assetReportId = response.body().getAssetReportId();
String assetReportToken = response.body().getAssetReportToken();

// Create an audit copy token for the Asset Report.
AssetReportAuditCopyCreateRequest auditCopyRequest = new AssetReportAuditCopyCreateRequest()
  .assetReportToken(assetReportToken)
  .auditorId("ocrolus");
Response<AssetReportAuditCopyCreateResponse> auditCopyResponse =
  plaidClient.assetReportAuditCopyCreate(auditCopyRequest).execute();
String auditCopyToken = auditCopyResponse.body().getAuditCopyToken();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`import plaid
from plaid.api import plaid_api
from plaid.model.item_public_token_exchange_request import ItemPublicTokenExchangeRequest
from plaid.model.processor_token_create_request import ProcessorTokenCreateRequest
from plaid.model.asset_report_create_request import AssetReportCreateRequest
from plaid.model.asset_report_create_request_options import AssetReportCreateRequestOptions
from plaid.model.asset_report_user import AssetReportUser
from plaid.model.asset_report_audit_copy_create_request import AssetReportAuditCopyCreateRequest

# Change sandbox to production to test with live users or to go live!
configuration = plaid.Configuration(
    host=plaid.Environment.Sandbox,
    api_key={
        'clientId': PLAID_CLIENT_ID,
        'secret': PLAID_SECRET,
        'plaidVersion': '2020-09-14'
    }
)
api_client = plaid.ApiClient(configuration)
client = plaid_api.PlaidApi(api_client)

# Exchange the public token from Plaid Link for an access token.
exchange_request = ItemPublicTokenExchangeRequest(public_token=public_token)
exchange_token_response = client.item_public_token_exchange(exchange_request)
access_token = exchange_token_response['access_token']

# Create a processor token for a specific account id.
create_request = ProcessorTokenCreateRequest(
    access_token=access_token,
    account_id=account_id,
    processor="ocrolus"
)
create_response = client.processor_token_create(create_request)
processor_token = create_response['processor_token']

# Create an Asset Report for the specific access token.
request = AssetReportCreateRequest(
    access_tokens=[access_token],
    days_requested=90,
    options=AssetReportCreateRequestOptions(
        webhook='https://www.example.com',
        client_report_id='123',
        user=AssetReportUser(
            client_user_id='7f57eb3d2a9j6480121fx361',
            first_name='Jane',
            middle_name='Leah',
            last_name='Doe',
            ssn='123-45-6789',
            phone_number='(555) 123-4567',
            email='jane.doe@example.com',
        )
    )
)
response = client.asset_report_create(request)
asset_report_id = response['asset_report_id']
asset_report_token = response['asset_report_token']

# Create an audit copy token for the Asset Report.
request = AssetReportAuditCopyCreateRequest(
    asset_report_token=asset_report_token,
    auditor_id='ocrolus'
)
response = client.asset_report_audit_copy_create(request)
audit_copy_token = response['audit_copy_token']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`import (
  "context"
  "os"

  plaid "github.com/plaid/plaid-go/v48/plaid"
)

// create the client
configuration := plaid.NewConfiguration()
configuration.AddDefaultHeader("PLAID-CLIENT-ID", os.Getenv("CLIENT_ID"))
configuration.AddDefaultHeader("PLAID-SECRET", os.Getenv("SECRET"))
configuration.UseEnvironment(plaid.Sandbox)
client := plaid.NewAPIClient(configuration)
ctx := context.Background()

// exchange the public_token for an access_token
exchangePublicTokenResp, _, err := client.PlaidApi.ItemPublicTokenExchange(ctx).ItemPublicTokenExchangeRequest(
  *plaid.NewItemPublicTokenExchangeRequest("PUBLIC_TOKEN"),
).Execute()
accessToken := exchangePublicTokenResp.GetAccessToken()

// get the account id
accountsResp, _, err := client.PlaidApi.AccountsGet(ctx).AccountsGetRequest(plaid.AccountsGetRequest{
  AccessToken: accessToken,
}).Execute()
accountID := accountsResp.GetAccounts()[0].GetAccountId()

// create a processor token for the specific account id
ocrolusTokenResp, _, err := client.PlaidApi.ProcessorTokenCreate(ctx).ProcessorTokenCreateRequest(
  *plaid.NewProcessorTokenCreateRequest(accessToken, accountID, "ocrolus"),
).Execute()

// create an asset report
assetReportCreateRequest := plaid.NewAssetReportCreateRequest(20)
assetReportCreateRequest.SetAccessTokens([]string{accessToken})
assetReportCreateResp, _, err := client.PlaidApi.AssetReportCreate(ctx).AssetReportCreateRequest(*assetReportCreateRequest).Execute()
assetReportToken := assetReportCreateResp.GetAssetReportToken()

// create an audit copy token for the Asset Report.
auditCopyCreateRequest := plaid.NewAssetReportAuditCopyCreateRequest(assetReportToken)
auditCopyCreateRequest.SetAuditorId("ocrolus")
auditCopyCreateResponse, _, err := client.PlaidApi.AssetReportAuditCopyCreate(ctx).AssetReportAuditCopyCreateRequest(*auditCopyCreateRequest).Execute()
auditCopyToken := auditCopyCreateResponse.GetAuditCopyToken()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`import (
    "context"
    "os"

    plaid "github.com/plaid/plaid-go/v49"
    plaidclient "github.com/plaid/plaid-go/v49/client"
    "github.com/plaid/plaid-go/v49/option"
)

// create the client
client := plaidclient.NewClient(
    option.WithClientID(plaid.String(os.Getenv("CLIENT_ID"))),
    option.WithSecret(os.Getenv("SECRET")),
    option.WithBaseURL(plaid.Environments.Sandbox),
)

// exchange the public_token for an access_token
exchangePublicTokenResp, err := client.Item.ExchangePublicToken(ctx, &plaid.ItemPublicTokenExchangeRequest{
    PublicToken: "PUBLIC_TOKEN",
})
accessToken := exchangePublicTokenResp.AccessToken

// get the account id
accountsResp, err := client.Accounts.Get(ctx, &plaid.AccountsGetRequest{
    AccessToken: accessToken,
})
accountID := accountsResp.Accounts[0].AccountID

// create a processor token for the specific account id
ocrolusTokenResp, err := client.Processor.CreateToken(ctx, &plaid.ProcessorTokenCreateRequest{
    AccessToken: accessToken,
    AccountID:   accountID,
    Processor:   "ocrolus",
})

// create an asset report
assetReportCreateRequest := &plaid.AssetReportCreateRequest{
    DaysRequested: 20,
}
assetReportCreateRequest.AccessTokens = []string{accessToken}
assetReportCreateResp, err := client.AssetReport.Create(ctx, assetReportCreateRequest)
assetReportToken := assetReportCreateResp.AssetReportToken

// create an audit copy token for the Asset Report.
auditCopyCreateRequest := &plaid.AssetReportAuditCopyCreateRequest{
    AssetReportToken: assetReportToken,
}
auditCopyCreateRequest.AuditorID = plaid.String("ocrolus")
auditCopyCreateResponse, err := client.AssetReport.CreateAuditCopy(ctx, auditCopyCreateRequest)
auditCopyToken := auditCopyCreateResponse.AuditCopyToken
`}</code></pre>
    </MDXCodeBlock>
  
    <p>{`For a valid request, the API will return a JSON response similar to:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample response",
        "#Sample": true,
        "response": true
      }}>{`{
  "processor_token": "processor-sandbox-0asd1-a92nc",
  "request_id": "UNIQUE_REQUEST_ID"
}
`}</code></pre>
    <p>{`For a valid `}<inlineCode parentName="p">{`audit_copy_token`}</inlineCode>{` request, the API will return a JSON response similar to:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Sample Audit Copy token response",
        "#Sample": true,
        "Audit": true,
        "Copy": true,
        "token": true,
        "response": true
      }}>{`{
  "audit_copy_token": "a-sandbox-3TAU2CWVYBDVRHUCAAAI27ULU4",
  "request_id": "UNIQUE_REQUEST_ID"
}
`}</code></pre>
    <p>{`For more information on creating Asset Report `}<inlineCode parentName="p">{`audit_copy_tokens`}</inlineCode>{`, see the documentation for the `}<a parentName="p" {...{
        "href": "/docs/assets"
      }}>{`Assets`}</a>{` product.`}</p>
    <h5 {...{
      "id": "testing-your-ocrolus-integration"
    }}>{`Testing your Ocrolus integration`}</h5>
    <p>{`You can create Ocrolus `}<inlineCode parentName="p">{`processor_tokens`}</inlineCode>{` in Sandbox (sandbox.plaid.com, allows testing with simulated users). To test the integration in Sandbox mode, use the Plaid `}<a parentName="p" {...{
        "href": "/docs/sandbox/test-credentials"
      }}>{`Sandbox credentials`}</a>{` when launching Link with a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` created in the Sandbox environment.`}</p>
    <p>{`To move to Production, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/settings/team/products"
      }}>{`request access`}</a>{` from the Dashboard. You will want to ensure that you have valid Ocrolus Production credentials prior to connecting bank accounts in the Ocrolus API with Plaid.`}</p>
    <h4 {...{
      "id": "support-and-questions"
    }}>{`Support and questions`}</h4>
    <p>{`Find answers to many common integration questions and concerns—such as pricing, sandbox and test mode usage, and more, in our `}<a parentName="p" {...{
        "href": "/docs"
      }}>{`docs`}</a>{`.`}</p>
    <p>{`If you're still stuck, open a `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/support/new"
      }}>{`support ticket`}</a>{` with information describing the issue that you're experiencing and we'll get back to you as soon as we can.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "getting-started",
        level: 4,
        title: "Getting started",
      },
{
        id: "instructions",
        level: 4,
        title: "Instructions",
      },
{
        id: "support-and-questions",
        level: 4,
        title: "Support and questions",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
